/**
 * EditableBoolean — boolean tap-to-toggle primitive (Phase 1.9.4 Item #4).
 *
 * Used for: dementia_unit (provider_organizations.dementia_unit boolean | null).
 *
 * Phase 1.9.4 design plan §2 (r2 locked):
 *   - 3 schema states (TRUE / FALSE / NULL) collapse to 2 display states:
 *     populated-TRUE (chip + X-close) vs. empty (NULL or FALSE both render
 *     the "+ Add" affordance). FALSE-as-affirmative ("we confirmed no
 *     secure dementia unit") is a separate operational concept that
 *     would earn its own UI affordance if signal materializes; not in
 *     scope here.
 *   - X-close → save(null), NOT save(false). Reverts to "not assessed"
 *     per the 0058 schema NULL semantic (locked at r2 Disposition #1;
 *     CTO ACCEPT).
 *   - "+ Add" affordance → save(true). The only path to TRUE.
 */

import { useState, type ReactNode } from 'react';
import { Chip, CHIP_VARIANT_CLASSES } from './primitives/Chip';
import { SavingIndicator } from './SavingIndicator';
import { pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import type { ChipVariant } from './EditableSelect';

export interface EditableBooleanProps {
  /** Current saved value. TRUE renders the chip; FALSE/NULL both render empty-state. */
  value: boolean | null;
  /** Save the new value. NULL on X-close per r2 Disposition #1. */
  onSave: (newValue: boolean | null, priorValue: boolean | null) => Promise<void>;
  /** ARIA label + display label for the chip + "+ Add {label}" empty-state stem. */
  label: string;
  /** Display text on the chip when TRUE (defaults to `label`). */
  trueLabel?: string;
  /** Chip variant when TRUE (e.g. 'dementia'). */
  trueVariant?: ChipVariant;
  /** Icon prefix on the TRUE chip (e.g. '🔒'). */
  trueIcon?: string;
  /** Role gate. False → read-only display. */
  canEdit?: boolean;
}

const SAVED_INDICATOR_MS = 1500;

export function EditableBoolean(props: EditableBooleanProps): ReactNode {
  const {
    value,
    onSave,
    label,
    trueLabel,
    trueVariant = 'dementia',
    trueIcon,
    canEdit = false,
  } = props;

  const [saveState, setSaveState] = useState<'saving' | 'saved' | 'error' | null>(
    null,
  );
  const [saveError, setSaveError] = useState<string | null>(null);
  const contact = useSupportContact();

  const isTrue = value === true;
  const chipText = trueLabel ?? label;

  async function commit(newValue: boolean | null): Promise<void> {
    setSaveState('saving');
    setSaveError(null);
    try {
      await onSave(newValue, value);
      setSaveState('saved');
      setTimeout(() => setSaveState(null), SAVED_INDICATOR_MS);
    } catch (e) {
      setSaveState('error');
      setSaveError(pageWriteText(e, contact));
    }
  }

  // Read-only path (canEdit=false).
  if (!canEdit) {
    if (isTrue) {
      return (
        <Chip variant={trueVariant}>
          {trueIcon ? `${trueIcon} ${chipText}` : chipText}
        </Chip>
      );
    }
    return <Chip variant="neutral">{label}: not specified</Chip>;
  }

  // Editable path.
  return (
    <span className="inline-flex items-center gap-1">
      {isTrue ? (
        <span className="inline-flex items-center gap-1">
          <span
            className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-medium ${chipBackgroundForVariant(trueVariant)}`}
          >
            {trueIcon ? `${trueIcon} ${chipText}` : chipText}
          </span>
          <button
            type="button"
            onClick={() => void commit(null)}
            aria-label={`Clear ${label}`}
            className="text-ink-soft hover:text-ink cursor-pointer text-xs leading-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded-sm"
          >
            ✕
          </button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => void commit(true)}
          aria-label={`Add ${label}`}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-sage-300 px-2.5 py-1 text-[11px] font-medium text-sage-600 hover:bg-sage-50 hover:border-sage-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 transition-colors"
        >
          + Add {label}
        </button>
      )}
      <SavingIndicator state={saveState} errorMessage={saveError ?? undefined} />
    </span>
  );
}

/** UX-13 S3 — the chip surface derives from the Chip primitive's exported
 *  variant map (CHIP_VARIANT_CLASSES). The previous hand-copied switch here
 *  drifted to the retired Tailwind hues (green/amber/blue/cyan/rose) when the
 *  primitive moved to the brand identity set — edit-mode chips rendered the
 *  old palette beside read-only brand chips. One map, no mirrors. */
function chipBackgroundForVariant(variant: ChipVariant): string {
  return CHIP_VARIANT_CLASSES[variant] ?? CHIP_VARIANT_CLASSES.neutral;
}
